import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
} from '@reduxjs/toolkit';
import { getTeams, GetTeamsResponse } from 'api/teams';
import { RootState, ThunkApi } from 'state/store';
import { LdTeam } from 'types/store/normalized/ldTeam';
import { SLICE_STATUS } from 'utils/constants';
import { Status } from 'utils/customTypes';

const adapter = createEntityAdapter<LdTeam>();

interface LdTeamsState {
  status?: Status;
  links?: {
    next?: string;
  };
  page?: {
    limit: number;
    cursor?: string;
  };
  selectedLdTeamIds?: string[];
}

const initialState: LdTeamsState = {};

/* ============================== REDUX THUNK =============================== */
const concurrencyCondition = (
  thunkParameters: unknown,
  { getState }: { getState: () => RootState }
): boolean => {
  const { status } = getLdTeamsState(getState());
  return status !== SLICE_STATUS.LOADING;
};

export const getLdTeamsList = createAsyncThunk<
  GetTeamsResponse,
  void,
  ThunkApi
>(
  'projectsList/filters/ldTeams/getLdTeamsList',
  async (_, { getState }) => {
    const state = getState() as RootState;
    const { links, page = { limit: 20 } } = state.projectsList.filters.ldTeams;
    let url = '/v2/teams/learning';
    if (links?.next) {
      url = links.next;
    } else {
      url += `?limit=${page.limit}`;
    }
    const response = await getTeams({ url });
    return response;
  },
  { condition: concurrencyCondition }
);

export const getSelectedLdTeamsList = createAsyncThunk<
  { data: LdTeam[]; selected: string[] },
  { selectedLdTeamIds: string[] },
  ThunkApi
>(
  'projectsList/filters/ldTeams/getSelectedLdTeamsList',
  async ({ selectedLdTeamIds }, { getState }) => {
    const state = getState() as RootState;
    const allLdTeamIds = state.projectsList.filters.ldTeams.ids as string[];
    const ldTeamIdsToFetch = selectedLdTeamIds.filter(
      (id) => !allLdTeamIds.includes(id)
    );
    let url = '/v2/teams/learning';
    if (ldTeamIdsToFetch.length > 0) {
      const searchParams = new URLSearchParams();
      ldTeamIdsToFetch.forEach((id) => {
        searchParams.append('filter[ids][]', id);
      });
      url += `?${searchParams.toString()}`;
    }
    const response = await getTeams({ url });
    return {
      data: response.teams,
      selected: selectedLdTeamIds,
    };
  }
);

const ldTeamsSlice = createSlice({
  name: 'projectsList/filters/ldTeams',
  initialState: adapter.getInitialState(initialState),
  reducers: {
    setSelectedLdTeamIds: (state, action) => {
      state.selectedLdTeamIds = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getLdTeamsList.fulfilled, (state, action) => {
        adapter.upsertMany(state, action.payload.teams);
        state.links = action.payload.links;
        state.status = SLICE_STATUS.SUCCESS;
      })
      .addCase(getLdTeamsList.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(getLdTeamsList.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      })
      .addCase(getSelectedLdTeamsList.fulfilled, (state, action) => {
        adapter.upsertMany(state, action.payload.data);
        state.selectedLdTeamIds = action.payload.selected;
      });
  },
});

const getLdTeamsState = (state: RootState) =>
  state.projectsList.filters.ldTeams;

export const { selectAll: selectAllLdTeams } =
  adapter.getSelectors<RootState>(getLdTeamsState);

export const selectCanFetchMoreLdTeams = (state: RootState) => {
  return getLdTeamsState(state).links?.next ? true : false;
};

export const selectSelectedLdTeamIds = (state: RootState) =>
  getLdTeamsState(state).selectedLdTeamIds || [];

export const selectSelectedLdTeams = createSelector(
  [selectSelectedLdTeamIds, selectAllLdTeams],
  (selectedLdTeamIds: string[], allLdTeams: LdTeam[]) => {
    return allLdTeams.filter((team) => selectedLdTeamIds.includes(team.id));
  }
);

export const { setSelectedLdTeamIds } = ldTeamsSlice.actions;

export default ldTeamsSlice.reducer;
